import React, { useEffect, useId, useRef } from 'react';
import CloseButton from './CloseButton';
import { useMediaQuery } from '../../utils/useMediaQuery';

/**
 * 공용 다이얼로그(모달). 사용법은 docs/ui-components.md 참고.
 * 예전 props(open, onClose, title, children, actions)는 그대로 동작하고, 나머지는 모두 선택이다.
 * 모양: 모서리 16px, 안쪽 여백 24px, 제목 20px, 구분선 없음 (화면마다 직접 만들던 모달과 같은 모양).
 */
interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** 아래쪽 버튼 영역. 보통 왼쪽 cancel, 오른쪽 submit */
  actions?: React.ReactNode;
  /** 제목 오른쪽에 × 닫기 버튼을 보여 준다. 기본 false */
  showCloseButton?: boolean;
  /** Esc 키로 닫기. 기본 true */
  closeOnEsc?: boolean;
  /** 바깥 어두운 부분을 눌러 닫기. 기본 true. 입력 중인 폼이면 false 권장 */
  closeOnBackdrop?: boolean;
  /** 상자 너비. 예: 480, '560px' */
  width?: string | number;
  /** 상자 최대 너비. 기본 '90vw' */
  maxWidth?: string | number;
  /** 모바일(767px 이하)에서 화면 전체를 채운다. 기본 false */
  fullScreenOnMobile?: boolean;
  /** 상자 안쪽 여백. 기본 24 (모바일 전체 화면이면 20) */
  contentPadding?: string | number;
  /** 아래 버튼 정렬. 기본 'flex-end' */
  actionsAlign?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  zIndex?: number;
}

/** 열린 다이얼로그 순서. Esc는 맨 위 다이얼로그만 닫는다. */
const openDialogStack: string[] = [];

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  showCloseButton = false,
  closeOnEsc = true,
  closeOnBackdrop = true,
  width,
  maxWidth = '90vw',
  fullScreenOnMobile = false,
  contentPadding,
  actionsAlign = 'flex-end',
  zIndex = 1300,
}) => {
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isFullScreen = fullScreenOnMobile && isMobile;
  const pressStartedOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc 닫기 + 뒤 화면 스크롤 막기
  useEffect(() => {
    if (!open) return undefined;
    openDialogStack.push(dialogId);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!closeOnEsc || event.key !== 'Escape') return;
      if (openDialogStack[openDialogStack.length - 1] !== dialogId) return;
      event.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openDialogStack.lastIndexOf(dialogId);
      if (index !== -1) openDialogStack.splice(index, 1);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, closeOnEsc, dialogId]);

  if (!open) return null;

  const showHeader = title != null || showCloseButton;
  const boxPadding = contentPadding ?? (isFullScreen ? 20 : 24);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex,
      }}
      // 안쪽에서 드래그하다 바깥에서 손을 떼도 닫히지 않게, 누르기 시작한 곳이 바깥일 때만 닫는다.
      onMouseDown={(e) => {
        pressStartedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (closeOnBackdrop && pressStartedOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
        pressStartedOnBackdrop.current = false;
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? titleId : undefined}
        // 예전처럼 안쪽 클릭이 다이얼로그 밖(부모 요소)으로 퍼지지 않게 한다.
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--Color-background-normal, #fff)',
          borderRadius: isFullScreen ? 0 : 16,
          padding: boxPadding,
          width: isFullScreen ? '100%' : width,
          height: isFullScreen ? '100%' : undefined,
          maxWidth: isFullScreen ? '100%' : maxWidth,
          maxHeight: isFullScreen ? '100%' : '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        {showHeader && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 20,
              flexShrink: 0,
            }}
          >
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                lineHeight: '30px',
                color: 'var(--Color-text-normal, #161616)',
                minWidth: 0,
              }}
            >
              {title}
            </h2>
            {showCloseButton && <CloseButton onClick={onClose} style={{ marginRight: -8 }} />}
          </div>
        )}
        <div style={{ flex: 1 }}>{children}</div>
        {actions != null && (
          <div
            style={{
              display: 'flex',
              justifyContent: actionsAlign,
              gap: 8,
              marginTop: 24,
              flexShrink: 0,
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog;
